<!--
  TinoDashboard.vue — Orchestrator for the Tino AI dashboard.
  Fetches all data, manages period state, composes sub-components.
-->
<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useChatStore } from '@/stores/ChatStore'
import { useOrderStore } from '@/stores/OrderStore'

import TinoBriefing          from './tino/TinoBriefing.vue'
import TinoKPICards          from './tino/TinoKPICards.vue'
import TinoChatTable         from './tino/TinoChatTable.vue'
import TinoActionQueue       from './tino/TinoActionQueue.vue'
import TinoActivityTimeline  from './tino/TinoActivityTimeline.vue'
import TinoPerformanceChart  from './tino/TinoPerformanceChart.vue'
import TinoLearningCurve     from './tino/TinoLearningCurve.vue'
import TinoSalesFunnel       from './tino/TinoSalesFunnel.vue'

const { t } = useI18n()
const period = ref('week')

// Stores
const chatStore  = useChatStore()
const orderStore = useOrderStore()
const { conversations }  = storeToRefs(chatStore)
const { orders }         = storeToRefs(orderStore)

// Full-bleed layout override
const FULLBLEED_CLASS = 'tino-dashboard-fullbleed'
onMounted(async () => {
  document.querySelector('.layout-page-content')?.classList.add(FULLBLEED_CLASS)
  await Promise.allSettled([
    chatStore.fetchSidebar?.(),
    orderStore.fetchOrders?.(),
  ])
})
onBeforeUnmount(() => {
  document.querySelector('.layout-page-content')?.classList.remove(FULLBLEED_CLASS)
})
</script>

<template>
  <div class="tino-dashboard">
    <!-- Page title row -->
    <div class="tino-dashboard-header">
      <div>
        <h5 class="text-h5 font-weight-bold mb-1">{{ t('dashboard.tino.pageTitle') }}</h5>
        <p class="text-sm text-medium-emphasis mb-0">
          {{ t('dashboard.tino.pageSubtitle') }}
        </p>
      </div>
    </div>

    <!-- Briefing -->
    <TinoBriefing v-model:period="period" class="mb-5" />

    <!-- KPI strip -->
    <TinoKPICards
      :period="period"
      :orders="orders"
      :conversations="conversations"
      class="mb-5"
    />

    <!-- Mid row: chat table + action queue -->
    <div class="tino-mid-row mb-5">
      <TinoChatTable class="tino-col-wide" />
      <TinoActionQueue class="tino-col-narrow" />
    </div>

    <!-- Funnel row -->
    <TinoSalesFunnel
      :orders="orders"
      class="mb-5"
    />

    <!-- Bottom row: timeline + radar + area chart -->
    <div class="tino-bottom-row">
      <TinoActivityTimeline />
      <TinoPerformanceChart />
      <TinoLearningCurve />
    </div>
  </div>
</template>

<style lang="scss">
/* Full-bleed override — unscoped so it applies globally when this view is active */
.tino-dashboard-fullbleed {
  padding: 0 !important;
}
</style>

<style scoped lang="scss">
.tino-dashboard {
  padding: 28px 32px 48px;
  background: #f4f6fb;
  min-block-size: 100vh;
}

.tino-dashboard-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-block-end: 24px;
}

/* Mid row: chat table takes ~60%, action queue ~40% */
.tino-mid-row {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
}

/* Bottom row: 3 equal columns */
.tino-bottom-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
}
</style>
